import * as React from "react";
import { Helmet } from "react-helmet-async";

export interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSectionProps {
  heading?: string;
  intro?: string;
  faqs: FAQItem[];
  /** FAQPage JSON-LD is emitted only on the dedicated /faq page (owner SEO rule); defaults to off. */
  emitSchema?: boolean;
  className?: string;
}

/**
 * Renders a visible FAQ section with accessible <details> disclosures,
 * and emits matching FAQPage JSON-LD for search/AI engines.
 */
export const FAQSection: React.FC<FAQSectionProps> = ({
  heading = "Frequently Asked Questions",
  intro,
  faqs,
  emitSchema = false,
  className = "",
}) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.answer,
      },
    })),
  };

  return (
    <section className={`py-14 sm:py-16 px-4 sm:px-6 lg:px-12 ${className}`} aria-labelledby="faq-heading">
      {emitSchema && (
        <Helmet>
          <script type="application/ld+json">{JSON.stringify(schema)}</script>
        </Helmet>
      )}
      <div className="container mx-auto max-w-3xl">
        <h2
          id="faq-heading"
          className="font-playfair text-3xl md:text-4xl font-medium text-primary mb-3 text-center"
        >
          {heading}
        </h2>
        {intro && (
          <p className="font-inter text-muted-foreground text-center mb-10 max-w-2xl mx-auto">
            {intro}
          </p>
        )}
        <div className="space-y-3">
          {faqs.map((f, i) => (
            <details
              key={i}
              className="group rounded-lg border border-border/60 bg-card/50 p-4 sm:p-5 open:border-accent/40"
            >
              <summary className="cursor-pointer list-none font-inter font-medium text-foreground flex items-start justify-between gap-4">
                <h3 className="text-base sm:text-lg font-medium m-0">{f.question}</h3>
                <span className="mt-1 text-accent transition-transform group-open:rotate-45 text-2xl leading-none">
                  +
                </span>
              </summary>
              <div className="mt-3 text-muted-foreground leading-relaxed">
                {f.answer}
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
